/* Wrapper connecté de CreerAmeliorerModal : charge les classes du prof, gère
   l'upload de la pièce jointe et la création du document, puis redirige vers
   l'éditeur. Point d'intégration unique pour tout futur point de branchement
   (dashboard, fiche classe, bibliothèque…) — ceux-ci passent juste des defaults. */

import { useEffect, useState } from 'react';
import { useNavigate } from '../lib/routing';
import { api } from '../lib/api';
import CreerAmeliorerModal, {
  type CreationKind,
  type CreerAmeliorerClassroom,
  type CreerAmeliorerSelection,
} from './creer-ameliorer-modal';

interface Props {
  defaultKind?: CreationKind;
  defaultClassroomIds?: string[];
  onClose: () => void;
}

export default function CreerAmeliorerModalContainer({ defaultKind, defaultClassroomIds, onClose }: Props) {
  const navigate = useNavigate();
  const [classrooms, setClassrooms] = useState<CreerAmeliorerClassroom[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.teacher.myClassrooms.query()
      .then((rows) => {
        const list = rows as Array<{ id: string; name: string; subjects?: { id: string; label: string | null }[] }>;
        setClassrooms(list.map((c) => ({
          id: c.id,
          name: c.name,
          subject: c.subjects?.map((s) => s.label).filter(Boolean).join(', ') || null,
        })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (selection: CreerAmeliorerSelection) => {
    setSubmitting(true);
    setError(null);
    try {
      const attachment = selection.attachment;
      const result = await api.content.createFromModal.mutate({
        kind: selection.kind,
        classroomIds: selection.classroomIds,
        note: selection.note,
        attachment: attachment
          ? { ext: attachment.name.split('.').pop() ?? 'bin', mimeType: attachment.type || 'application/octet-stream' }
          : undefined,
      });

      if (attachment && result.upload) {
        const put = await fetch(result.upload.signedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': attachment.type || 'application/octet-stream' },
          body: attachment,
        });
        if (!put.ok) throw new Error(`Échec de l'envoi du fichier (${put.status})`);
        /* Classification auto du type — écrase silencieusement le choix initial
           de l'enseignant ; en cas d'échec le type initial reste en place. */
        await api.content.classifyKind.mutate({ documentId: result.document.id }).catch(() => {});
      }

      navigate(`/teacher/library/${result.document.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  };

  return (
    <>
      <CreerAmeliorerModal
        classrooms={classrooms}
        loading={loading}
        submitting={submitting}
        defaultKind={defaultKind}
        defaultClassroomIds={defaultClassroomIds}
        onClose={onClose}
        onSubmit={(sel) => void handleSubmit(sel)}
      />
      {error && (
        <div className="fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4">
          <div
            role="alert"
            className="max-w-[420px] rounded-[var(--naia-r-md)] bg-[var(--naia-error)] px-4 py-2.5 text-center text-[14px] font-semibold leading-[1.4] text-white shadow-[var(--naia-shadow-hero)]"
          >
            {error}
          </div>
        </div>
      )}
    </>
  );
}
